import { useCallback, useRef, useState } from 'react';

/**
 * Ошибки формы: подсветка поля, текст под ним и прокрутка к первому промаху.
 *
 * До этого форма ругалась модальным окном: человек читал «Заполните вес и
 * ставку», закрывал окно и заново искал глазами, что именно пустое, — а на
 * длинной форме публикации нужное поле вообще оставалось за краем экрана.
 * Поэтому ошибка живёт рядом с полем, а не поверх формы.
 *
 * Пользоваться так:
 *
 *   const form = useFormErrors();
 *   ...
 *   <input {...form.field('price')} value={price} onChange={...} />
 *   <FieldError form={form} name="price" />
 *   ...
 *   if (!price) return form.fail({ price: 'Укажите ставку' });
 */

export type FieldErrors = Record<string, string>;

export function useFormErrors() {
  const [errors, setErrors] = useState<FieldErrors>({});

  // Узлы полей нужны, чтобы доскроллить до первого промаха. Ref, а не
  // querySelector по имени: имена полей на разных экранах повторяются,
  // и поиск по документу однажды найдёт чужое поле.
  const nodes = useRef(new Map<string, HTMLElement>());

  const register = useCallback((name: string) => {
    return (el: HTMLElement | null) => {
      if (el) nodes.current.set(name, el);
      else nodes.current.delete(name);
    };
  }, []);

  /**
   * Показать ошибки и подвести к первой.
   *
   * Возвращает `false`, чтобы вызывающий код читался как охранное условие:
   * `if (!price) return form.fail({ price: '…' })`.
   */
  const fail = useCallback((next: FieldErrors) => {
    setErrors(next);

    const first = Object.keys(next)[0];
    const el = first ? nodes.current.get(first) : null;

    if (el) {
      // Прокрутка на кадр позже: подпись с ошибкой появляется вместе с этим
      // же рендером и сдвигает разметку. Промотав до сдвига, попадём мимо.
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Фокус ставим только там, где он уместен: на select и input он
        // открывает клавиатуру на телефоне ровно тогда, когда человек уже
        // смотрит на нужное поле.
        const focusable = el.matches('input, select, textarea')
          ? el
          : el.querySelector<HTMLElement>('input, select, textarea');
        focusable?.focus({ preventScroll: true });
      });
    }

    return false;
  }, []);

  /** Снять ошибку с поля. Вызывается при вводе — иначе краснота залипает. */
  const clear = useCallback((name: string) => {
    setErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const reset = useCallback(() => setErrors({}), []);

  /**
   * Атрибуты для поля ввода: ссылка на узел, подсветка и связь с текстом
   * ошибки для программ чтения с экрана.
   */
  const field = useCallback(
    (name: string) => ({
      ref: register(name),
      'aria-invalid': errors[name] ? true : undefined,
      'aria-describedby': errors[name] ? `err-${name}` : undefined,
      onInput: () => clear(name),
    }),
    [errors, register, clear],
  );

  /** Обёртка для того, что полем ввода не является: чипы, группы кнопок. */
  const group = useCallback(
    (name: string) => ({
      ref: register(name),
      className: errors[name] ? 'invalid-group' : undefined,
    }),
    [errors, register],
  );

  return {
    errors,
    has: (name: string) => Boolean(errors[name]),
    field,
    group,
    fail,
    clear,
    reset,
  };
}
